// Images and embeds, and the tile of a retired widget. The custom widget an
// agent writes lives in custom.tsx, with its kit in kit/.

import { useCallback, useRef, useState } from "react";
import { ImagePlus, MonitorPlay, PencilRuler, Upload } from "lucide-react";
import { SPEC } from "../../shared/catalog";
import { cx, domainOf } from "../lib/util";
import { deleteItems, updateProps } from "../state/actions";
import { useStore } from "../state/store";
import type { WP } from "./data";
import { canFork, makeOwn } from "./fork";
import { Setup } from "./setup";

/** A tile of a widget the board no longer offers (the radio, a status with nothing to show): what happened, and a way to clear it or keep it as the user's own. */
export function Retired({ item, readOnly }: WP) {
  const own = !readOnly && canFork(item);
  return (
    <div className="empty retired">
      <span className="empty-title">{SPEC[item.type]?.label ?? "This widget"} was retired</span>
      <span className="empty-sub">{own ? "The board no longer has it. Remove it, or make it your own to change it freely." : "The board no longer has it. Nothing else on your board changed."}</span>
      {!readOnly && (
        <div className="empty-actions">
          <button className="btn btn-sm" onClick={() => deleteItems([item.id])} data-interactive>
            Remove
          </button>
          {own && (
            <button className="btn btn-sm" onClick={() => makeOwn(item.id)} data-interactive>
              <PencilRuler size={13} /> Make it my own
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export async function fileToDataUrl(file: File, max = 1600): Promise<string> {
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * k);
  c.height = Math.round(bmp.height * k);
  c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
  for (const q of [0.86, 0.75, 0.6, 0.45]) {
    const url = c.toDataURL("image/webp", q);
    if (url.length < 1_150_000) return url;
  }
  throw new Error("That image is too large, even after shrinking it.");
}

export function ImageW({ item, readOnly }: WP) {
  const src = String(item.props.src ?? "");
  const fit = item.props.fit === "contain" ? "contain" : "cover";
  const [url, setUrl] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  // the picture a link gave could not be drawn: said, never a broken image
  const [broken, setBroken] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    try {
      updateProps(item.id, { src: await fileToDataUrl(f) }, "Set image");
      setProblem(null);
    } catch (e) {
      setProblem((e as Error).message);
    }
  };
  if (src && broken !== src) return <img className="image" src={src} alt={item.title} style={{ objectFit: fit }} draggable={false} onError={() => setBroken(src)} />;
  if (readOnly)
    return (
      <div className="image-empty">
        <span className="faint">No image</span>
      </div>
    );
  return (
    <div
      className="image-empty"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void pick(e.dataTransfer.files[0]);
      }}
    >
      <Setup icon={<ImagePlus />} title={src ? "That picture did not load" : "Add a picture"} sub={src ? "Its link gave nothing a browser can show. Upload it, or paste another link." : "Upload one, drop it here, or paste a link."} problem={problem}>
        <button className="btn btn-sm btn-primary" onClick={() => input.current?.click()} aria-label="Upload an image" title="Upload an image">
          <Upload size={13} /> Upload
        </button>
        <form
          className="setup-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^https:\/\//.test(url.trim())) return setProblem("Paste the whole link, starting with https://.");
            setProblem(null);
            setBroken(null);
            updateProps(item.id, { src: url.trim() }, "Set image");
            setUrl("");
          }}
        >
          <input placeholder="or paste an https:// link" aria-label="A picture's link" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
        </form>
      </Setup>
      <input ref={input} type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0])} />
    </div>
  );
}

export function embedUrl(raw: string): string | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "https:") return null;
  const h = u.hostname.replace(/^www\./, "");
  if (h === "youtube.com" || h === "m.youtube.com") {
    const id = u.searchParams.get("v") ?? (u.pathname.startsWith("/shorts/") ? u.pathname.split("/")[2] : null);
    if (id) return `https://www.youtube.com/embed/${id}`;
  }
  if (h === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
  if (h === "loom.com" && u.pathname.startsWith("/share/")) return `https://www.loom.com/embed/${u.pathname.split("/")[2]}`;
  if (h === "vimeo.com" && /^\/\d+/.test(u.pathname)) return `https://player.vimeo.com/video/${u.pathname.slice(1)}`;
  if (h === "figma.com" && !u.pathname.startsWith("/embed")) return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(u.toString())}`;
  if (h === "open.spotify.com" && !u.pathname.startsWith("/embed")) return `https://open.spotify.com/embed${u.pathname}`;
  if (h === "docs.google.com") return u.toString().replace(/\/(edit|view)(\?.*)?$/, "/preview");
  return u.toString();
}

export function Embed({ item, readOnly }: WP) {
  const url = String(item.props.url ?? "");
  const src = url ? embedUrl(url) : null;
  const selected = useStore((s) => s.selection.includes(item.id) && s.selection.length === 1);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  // the link box takes the keys only when the tile was just added, and never moves the board to itself: an empty
  // embed focused on every load scrolled the canvas to it each time the board opened
  const ask = useCallback((el: HTMLInputElement | null) => {
    if (el && Date.now() - item.createdAt < 10_000) el.focus({ preventScroll: true });
  }, [item.createdAt]);
  if (!src && readOnly)
    return (
      <div className="image-empty">
        <span className="faint">Nothing embedded</span>
      </div>
    );
  if (!src)
    return (
      <div className="image-empty">
        <Setup
          icon={<MonitorPlay />}
          title="Show a video or page"
          sub="Paste a YouTube, Loom, Vimeo, Figma, Spotify or Google Docs link. Many other sites do not allow showing them inside another page."
          problem={problem}
        >
          <form
            className="setup-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (embedUrl(draft)) updateProps(item.id, { url: draft.trim() }, "Embed");
              else setProblem("Paste the whole link, starting with https://.");
            }}
          >
            <input ref={ask} placeholder="https://www.youtube.com/watch?v=…" aria-label="A video's or page's link" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
            <button className="btn btn-sm btn-primary" type="submit" disabled={!draft.trim()}>
              Show
            </button>
          </form>
        </Setup>
      </div>
    );
  return (
    <div className={cx("embed", (selected || readOnly) && "is-live")}>
      <iframe
        src={src}
        title={item.title || domainOf(src)}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write"
        referrerPolicy="strict-origin-when-cross-origin"
        loading="lazy"
      />
      {!selected && !readOnly && <div className="embed-cover">Click to use</div>}
    </div>
  );
}
